import React from 'react';
import { Droplet, Flower, ScrollText } from 'lucide-react';

/**
 * ThreeKProtocol — Kewra · Kamal · Karma.
 *
 * The core biological-and-digital cycle of the house, named once and
 * reused across philosophy, loyalty, and partner surfaces.
 *
 *   K1 · Kewra  — the subtle signal (hydrosol base; the molecule)
 *   K2 · Kamal  — the emergent state (lotus; the biology that arrives)
 *   K3 · Karma  — the verifiable record ($SEVA token; the on-chain hash)
 *
 * Light variant by default; pass `variant="dark"` for steel-blue surfaces.
 */

const STAGES = [
  {
    id: 'kewra',
    icon: Droplet,
    glyph: 'K¹',
    devanagari: 'केवड़ा',
    meaning: 'Pandanus odoratissimus · the white-flower hydrosol',
    role: 'The Subtle Signal',
    body:
      '100 % Kewra hydrosol is the base of every Shreesavaa formula. Not water, never water. A signal carried by oligomeric molecules captured from sacrificed steam — the chemistry the Vedas named long before chromatography could measure it.',
    chip: 'Hydrosol · signal',
  },
  {
    id: 'kamal',
    icon: Flower,
    glyph: 'K²',
    devanagari: 'कमल',
    meaning: 'Nelumbo nucifera · the lotus that rises through silt',
    role: 'The Emergent State',
    body:
      'The biology that arrives in the guest. Skin lifted to its lotus state — radiant, unirritated, in rhythm with the circadian window of repair. Kamal is what Vedic Biophysics is for: a measurable shift in the dermal system at the named hour.',
    chip: 'State · lotus',
  },
  {
    id: 'karma',
    icon: ScrollText,
    glyph: 'K³',
    devanagari: 'कर्म',
    meaning: 'Karma · the action signed and witnessed',
    role: 'The Verifiable Record',
    body:
      'Every ritual completed mints a $SEVA token against the Guardian\'s record. Indra, the Ledger Witness, signs the action onto the 211 Million supply. No claim of effect that cannot be pointed to a sensor or a hash.',
    chip: 'Token · record',
  },
];

const ThreeKProtocol = ({
  testId   = 'three-k-protocol',
  overline = 'The 3K Protocol · Signal · State · Record',
  variant  = 'light',
}) => {
  const isDark = variant === 'dark';
  const bgCls = isDark ? 'bg-[#7C9CB1] text-[#F4F8F9]' : 'bg-[#F4F8F9] text-[#7C9CB1]';
  const overlineCls = isDark ? 'text-[#F4F8F9]/75' : 'text-[#90B0BF]';
  const cardBg = isDark
    ? 'bg-[#7C9CB1] border-[#F4F8F9]/30'
    : 'bg-[#F4F8F9] border-[#C1D7E2]';
  const subtleText = isDark ? 'text-[#F4F8F9]/85' : 'text-[#7C9CB1]/85';
  const accentText = isDark ? 'text-[#F4F8F9]/80' : 'text-[#90B0BF]';
  const heading = isDark ? 'text-[#F4F8F9]' : 'text-[#7C9CB1]';

  return (
    <section
      id="three-k-protocol"
      data-testid={testId}
      className={`px-6 md:px-12 py-24 md:py-32 border-t ${
        isDark ? 'border-[#F4F8F9]/25' : 'border-[#C1D7E2]'
      } ${bgCls}`}
    >
      <div className="max-w-[1400px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 mb-14">
          <div className="md:col-span-7">
            <p
              data-testid={`${testId}-overline`}
              className={`font-mono text-[10px] tracking-[0.32em] uppercase ${overlineCls} mb-6`}
            >
              <span className={`inline-block w-7 h-px ${isDark ? 'bg-[#F4F8F9]/75' : 'bg-[#90B0BF]'} mr-3 align-middle`} />
              {overline}
            </p>
            <h2 className={`font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] ${heading}`}>
              Three letters,
              <br />
              one <em className={accentText}>cycle</em>.
            </h2>
          </div>
          <div className="md:col-span-5 md:pt-4">
            <p
              data-testid={`${testId}-thesis`}
              className={`font-serif italic text-lg md:text-xl leading-relaxed max-w-md ${subtleText}`}
            >
              <strong className="not-italic font-serif">Kewra</strong> carries the signal.
              {' '}<strong className="not-italic font-serif">Kamal</strong> is the state it produces.
              {' '}<strong className="not-italic font-serif">Karma</strong> is the record we leave behind.
            </p>
            <p className={`mt-5 font-light leading-relaxed max-w-md ${subtleText}`}>
              The complete biology, with its digital footprint. Nothing
              claimed, nothing promised — only signal, state, and record.
            </p>
          </div>
        </div>

        <ol className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {STAGES.map((s) => {
            const Icon = s.icon;
            return (
              <li
                key={s.id}
                data-testid={`${testId}-${s.id}`}
                className={`relative border p-7 md:p-9 flex flex-col ${cardBg}`}
              >
                <span
                  aria-hidden
                  className="absolute top-0 left-7 right-7 h-px"
                  style={{
                    background:
                      'linear-gradient(90deg, transparent, #D4AF5A 35%, #D4AF5A 65%, transparent)',
                  }}
                />
                <div className="flex items-baseline justify-between mb-5">
                  <p className={`font-mono text-[10px] tracking-[0.3em] uppercase ${overlineCls}`}>
                    {s.glyph}
                  </p>
                  <p className={`font-serif text-2xl ${accentText}`}>{s.devanagari}</p>
                </div>
                <Icon size={24} strokeWidth={1.2} className={`${heading} mb-5`} />
                <h3 className={`font-serif text-2xl md:text-3xl font-light leading-tight ${heading}`}>
                  {s.id === 'kewra' ? 'Kewra' : s.id === 'kamal' ? 'Kamal' : 'Karma'}
                </h3>
                <p className={`mt-2 font-serif italic text-base leading-relaxed ${accentText}`}>
                  {s.meaning}
                </p>
                <p className={`mt-4 font-mono text-[10px] tracking-[0.3em] uppercase ${overlineCls}`}>
                  {s.role}
                </p>
                <p className={`mt-3 font-light leading-relaxed ${subtleText}`}>
                  {s.body}
                </p>
                <p
                  data-testid={`${testId}-${s.id}-chip`}
                  className={`mt-6 pt-5 border-t ${isDark ? 'border-[#F4F8F9]/25' : 'border-[#C1D7E2]'} font-mono text-[10px] tracking-[0.28em] uppercase ${overlineCls}`}
                >
                  {s.chip}
                </p>
              </li>
            );
          })}
        </ol>

        <p
          data-testid={`${testId}-signature`}
          className={`mt-14 font-mono text-[10px] tracking-[0.3em] uppercase ${overlineCls} text-center`}
        >
          Kewra is what enters · Kamal is what arrives · Karma is what remains
        </p>
      </div>
    </section>
  );
};

export default ThreeKProtocol;
